class ChamberelleHeroVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.button = this.querySelector('button');
    this.viewport = matchMedia('(max-width: 749px)');
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.userPaused = this.motion.matches;
    this.inView = true;
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.viewport.addEventListener('change', this.selectSource, options);
    this.motion.addEventListener('change', this.motionChanged, options);
    document.addEventListener('visibilitychange', this.syncPlayback, options);
    this.button.addEventListener('click', this.togglePlayback, options);
    this.video.addEventListener('play', this.updateButton, options);
    this.video.addEventListener('playing', this.revealVideo, options);
    this.video.addEventListener('pause', this.updateButton, options);
    this.video.addEventListener('error', this.failed, options);
    this.observer = new IntersectionObserver(([entry]) => {
      this.inView = entry.isIntersecting;
      this.syncPlayback();
    });
    this.observer.observe(this);
    // Finish critical images and styles, then allow a paint before video traffic.
    if (document.readyState === 'complete') this.scheduleStart();
    else window.addEventListener('load', this.scheduleStart, { ...options, once: true });
  }
  disconnectedCallback() {
    cancelAnimationFrame(this.startFrame);
    this.ready = false;
    this.abort?.abort();
    this.observer?.disconnect();
    this.video?.pause();
  }
  scheduleStart = () => {
    this.startFrame = requestAnimationFrame(() => {
      this.startFrame = requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.ready = true;
        this.selectSource();
      });
    });
  };
  selectSource = () => {
    if (!this.ready) return;
    const template = this.querySelector(this.viewport.matches ? '[data-video-mobile]' : '[data-video-desktop]');
    this.video.pause();
    this.video.replaceChildren(template.content.cloneNode(true));
    this.video.muted = true;
    // The responsive picture beneath the player is the only poster download.
    // Keep it visible until the selected video has a frame ready to display.
    this.video.style.opacity = '0';
    this.hasSource = !!this.video.querySelector('source');
    this.video.hidden = !this.hasSource;
    this.button.hidden = !this.hasSource;
    this.video.load();
    this.syncPlayback();
    this.updateButton();
  };
  revealVideo = () => {
    this.video.style.removeProperty('opacity');
  };
  motionChanged = () => {
    this.userPaused = this.motion.matches;
    this.syncPlayback();
  };
  syncPlayback = () => {
    if (!this.hasSource || this.userPaused || !this.inView || document.hidden) {
      this.video.pause();
    } else {
      // Autoplay can be blocked by battery/data-saving settings. Keep a play button.
      this.video.play()?.catch(() => this.updateButton());
    }
  };
  togglePlayback = () => {
    this.userPaused = !this.video.paused;
    this.syncPlayback();
  };
  failed = () => {
    this.video.hidden = true;
    this.button.hidden = true;
  };
  updateButton = () => {
    const paused = this.video.paused;
    const label = paused ? 'Play background video' : 'Pause background video';
    this.button.setAttribute('aria-label', label);
    this.querySelector('[data-video-label]').textContent = label;
    this.querySelector('[data-video-play]').toggleAttribute('hidden', !paused);
    this.querySelector('[data-video-pause]').toggleAttribute('hidden', paused);
  };
}
if (!customElements.get('chamberelle-hero-video')) customElements.define('chamberelle-hero-video', ChamberelleHeroVideo);
